// Plain-English context for a number: national standing and own-history extremes. Pure, unit-tested.
import type { Level, NumberContext } from "./types.ts";
import { periodLabel, pctChange } from "./format.ts";
import { outlookOf } from "./outlook.ts";

const PLURAL: Record<Level, string> = { us: "", state: "states", metro: "metros", county: "counties", zip: "ZIPs", city: "cities" };

/** "Higher than 82% of ZIPs · highest since Mar 2013" (either part may be missing). */
export function contextText(level: Level, c: NumberContext | null | undefined): string {
  if (!c) return "";
  const parts: string[] = [];
  if (c.level_pct != null && c.level_n != null && c.level_n >= 5 && level !== "us") {
    // the share of the other areas below this one: 1 at the top ("Higher than 100%" read wrong), 0 at the bottom
    const p = Math.min(99, Math.max(1, Math.round(c.level_pct * 100)));
    const n = c.level_n.toLocaleString("en-US");
    parts.push(c.level_pct >= 1 ? `Highest of ${n} ${PLURAL[level]}` : c.level_pct <= 0 ? `Lowest of ${n} ${PLURAL[level]}`
      : p >= 50 ? `Higher than ${p}% of ${PLURAL[level]}` : `Lower than ${100 - p}% of ${PLURAL[level]}`);
  }
  if (c.since) {
    parts.push(c.since.period
      ? `${c.since.kind} since ${periodLabel(c.since.period)}`
      : `${c.since.kind} on record (since ${periodLabel(c.first)})`);
  }
  const s = parts.join(" · ");
  return s ? s[0]!.toUpperCase() + s.slice(1) : "";
}

/** An area's resolved forecasts (Api.scores().track): direction right, the model's and the naive guess's. */
export interface TrackRecord {
  calls: number;
  right_direction: number;
  naive_right_direction: number;
  /** months with a naive guess (a value 12 months before); `calls` where missing */
  naive_calls?: number;
  first: string;
}

/** Below this many resolved months (a year of monthly calls), an area's direction record is too short to judge. */
export const MIN_TRACK = 12;

/**
 * How far to trust the forecast's direction here, from its own record, scored as the scorecard headlines it: the share
 * of months it had the direction right vs. the naive guess ("the last 12 months repeat"). "low" when it doesn't beat
 * naive (a tie doesn't), "untested" when too few months have resolved.
 */
export type Confidence =
  | { kind: "untested"; calls: number }
  | { kind: "low" | "ok"; calls: number; model: number; naive: number; first: string };

export function forecastConfidence(t: TrackRecord | null | undefined): Confidence {
  if (!t || t.calls < MIN_TRACK) return { kind: "untested", calls: t?.calls ?? 0 };
  const nCalls = t.naive_calls ?? t.calls;
  const model = t.right_direction / t.calls, naive = nCalls ? t.naive_right_direction / nCalls : 0;
  // compared as the pages print them (whole percents), so "66% vs. 66%" is never called a win
  return { kind: Math.round(model * 100) > Math.round(naive * 100) ? "ok" : "low", calls: t.calls, model, naive, first: t.first };
}

const pc = (v: number) => `${Math.round(v * 100)}%`;

/**
 * The track-record line under the forecast (free, on the panel and the market pages):
 * low: "Low confidence here: in past years, simply assuming last year repeats called the direction better in Oakland (79% vs. 66% of 76 months)."
 * ok: "Its record here: the right direction in 61% of 76 months, vs. 50% for assuming last year repeats (backtest since Jun 2019)."
 * untested: "No track record here yet: too few of its past forecasts have been checked to say how far to trust it."
 */
export function confidenceText(name: string, c: Confidence): string {
  if (c.kind === "untested") return "No track record here yet: too few of its past forecasts have been checked to say how far to trust it.";
  if (c.kind === "ok") {
    return `Its record here: the right direction in ${pc(c.model)} of ${c.calls} months, vs. ${pc(c.naive)} for assuming last year repeats (backtest since ${periodLabel(c.first)}).`;
  }
  return pc(c.model) === pc(c.naive)
    ? `Low confidence here: in past years, simply assuming last year repeats called the direction as often as the model in ${name} (both ${pc(c.model)} of ${c.calls} months).`
    : `Low confidence here: in past years, simply assuming last year repeats called the direction better in ${name} (${pc(c.naive)} vs. ${pc(c.model)} of ${c.calls} months).`;
}

/** "Chance of a rise", or "Model's chance of a rise" where the record here doesn't back the model's own odds. */
export const riseLabel = (c: Confidence) => (c.kind === "ok" ? "Chance of a rise" : "Model's chance of a rise");

export interface ForecastChange {
  from: string;
  to: string;
  before: number | null;
  after: number | null;
  drivers: Array<{ key: string; label: string; effect: number | null; score: number | null; score_prev: number | null }>;
  refit: number | null;
}

const pts = (v: number) => `${v >= 0 ? "+" : "−"}${Math.abs(v * 100).toFixed(1)} pts`;

/**
 * "Since Jul 2026: −1.1% → −0.6% (+0.5 pts)" and the moves behind it, biggest first: drivers whose data moved it by
 * at least `min` (0.05 pts by default), at most `max` of them, then the model's monthly refit when it's that large.
 */
export function forecastChangeText(c: ForecastChange | null | undefined, max = 4, min = 0.0005) {
  if (!c || c.before == null || c.after == null) return null;
  const delta = c.after - c.before;
  const headline = `Since ${periodLabel(c.from)}: ${pctChange(c.before)} → ${pctChange(c.after)} (${Math.abs(delta) < 0.0005 ? "unchanged" : pts(delta)})`;
  const moves = c.drivers
    .filter((d) => d.effect != null && Math.abs(d.effect) >= min)
    .slice(0, max)
    .map((d) => ({
      label: d.label,
      effect: pts(d.effect!),
      up: d.effect! > 0,
      // the bar's move, when it has one
      bar: d.score != null && d.score_prev != null && Math.round(d.score) !== Math.round(d.score_prev) ? `${Math.round(d.score_prev)} → ${Math.round(d.score)}` : "",
    }));
  if (c.refit != null && Math.abs(c.refit) >= min) moves.push({ label: "Model refit", effect: pts(c.refit), up: c.refit > 0, bar: "" });
  return { headline, moves };
}

/** One plain sentence under the forecast's label: what it says about the chance of a rise (thresholds 1/3, 2/3). */
export function forecastReading(label: string, pRise: number | null, c?: Confidence): string {
  // where its record here trails the naive guess, the label is the model's lean, not a likely outcome
  const o = outlookOf(label);
  if (c?.kind === "low" && (o === "up" || o === "down")) {
    return `The model leans toward a ${o === "up" ? "rise" : "fall"} over the next year, but its record here is weak.`;
  }
  if (o === "up") return "Prices are more likely to rise than fall over the next year.";
  if (o === "down") return "Prices are more likely to fall than rise over the next year.";
  if (pRise == null) return "The model can't call the direction for the next year.";
  return pRise >= 0.5
    ? "A rise is somewhat more likely than a fall, but not by enough to call."
    : "A fall is somewhat more likely than a rise, but not by enough to call.";
}
